import { logger } from '../services/logger.ts';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/db.ts';

// Define special characters regex pattern
const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{};':|<>?,./`~]/;

export function UpdatePassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check session and handle URL parameters
  useEffect(() => {
    const handleTokenVerification = async () => {
      try {
        setSessionLoading(true);
        
        // First, check if there's already an active session
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        
        if (existingSession) {
          // Already have a valid session, no need to verify token again
          logger.info('Found existing session, skipping token verification');
          setHasValidSession(true);
          setSessionLoading(false);
          return;
        }
        
        // Log all URL parameters for debugging
        logger.info('Password reset URL parameters:', {
          href: window.location.href,
          search: window.location.search,
          hash: window.location.hash,
          allParams: Object.fromEntries(searchParams.entries())
        });
        
        // Check for Supabase error parameters first
        const error = searchParams.get('error');
        const errorCode = searchParams.get('error_code');
        const errorDescription = searchParams.get('error_description');
        
        if (error) {
          logger.error('Supabase returned error:', { error, errorCode, errorDescription });
          
          if (errorCode === 'otp_expired') {
            setError(
              'Your password reset link has expired. ' +
              'This usually happens when: (1) The link is older than 1 hour, ' +
              '(2) You\'ve already used this link, or ' +
              '(3) Your email template needs to be updated for PKCE flow. ' +
              'Please request a new reset link.'
            );
          } else if (errorCode === 'otp_disabled') {
            setError('This password reset link has already been used. Please request a new one.');
          } else {
            setError(errorDescription || 'Invalid password reset link. Please request a new one.');
          }
          setSessionLoading(false);
          return;
        }

        // For PKCE flow, we need to use token_hash instead of token
        const tokenHash = searchParams.get('token_hash');
        const type = searchParams.get('type');
        
        if (!tokenHash || type !== 'recovery') {
          // No token in URL - check if we landed here without proper authentication
          setError('Invalid password reset link. Please ensure your email template is configured for PKCE flow.');
          setSessionLoading(false);
          return;
        }

        // Verify the OTP using token_hash for PKCE flow (only runs once when token is present)
        try {
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'email_change' // Use 'email_change' type for password recovery in PKCE
          });

          if (verifyError) {
            logger.error('Token verification error:', verifyError);
            
            // Try with 'email' type as fallback
            const { data: fallbackData, error: fallbackError } = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: 'email'
            });

            if (fallbackError) {
              logger.error('Fallback token verification error:', fallbackError);
              setError('Failed to verify password reset token. ' + (fallbackError.message || 'Please request a new reset link.'));
              setSessionLoading(false);
              return;
            }

            if (fallbackData?.session) {
              setHasValidSession(true);
              setError(null);
              logger.info('Successfully verified password reset token with fallback method');
              // Clean up URL to remove token parameters
              window.history.replaceState({}, '', '/update-password');
              return;
            }
          }

          if (data?.session) {
            setHasValidSession(true);
            setError(null);
            logger.info('Successfully verified password reset token');
            // Clean up URL to remove token parameters
            window.history.replaceState({}, '', '/update-password');
          } else {
            setError('Failed to establish authentication session. Please request a new reset link.');
            setSessionLoading(false);
          }

        } catch (verifyErr) {
          logger.error('Error during token verification:', verifyErr);
          setError('Failed to verify password reset token. Please request a new reset link.');
          setSessionLoading(false);
        }

      } catch (err) {
        logger.error('Error during session verification:', err);
        setError('Failed to process password reset link. Please request a new reset link.');
      } finally {
        setSessionLoading(false);
      }
    };

    handleTokenVerification();
  }, [searchParams]);

  const handleRequestNewLink = () => {
    navigate('/auth/reset-password');
  };

  const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Check minimum length
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter (a-z)');
    }
    
    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter (A-Z)');
    }
    
    // Check for digit
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number (0-9)');
    }
    
    // Check for special character
    if (!SPECIAL_CHARS_REGEX.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    let strength = 0;
    
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (SPECIAL_CHARS_REGEX.test(password)) strength++;
    
    if (strength <= 2) return { strength: 1, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 2, label: 'Fair', color: 'bg-wayja-teal' };
    if (strength <= 5) return { strength: 3, label: 'Good', color: 'bg-yellow-500' };
    return { strength: 4, label: 'Strong', color: 'bg-green-500' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!hasValidSession) {
      setError('No valid authentication session. Please request a new reset link.');
      return;
    }

    setLoading(true);
    setError(null);

    // Validate password
    const validation = validatePassword(password);
    if (!validation.isValid) {
      setError(validation.errors.join('. '));
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        logger.error('Password update error:', updateError);
        throw updateError;
      }

      setSuccess(true);
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/auth/login', { 
          state: { 
            message: 'Password updated successfully! Please sign in with your new password.' 
          }
        });
      }, 2000);

    } catch (err) {
      logger.error('Error updating password:', err);
      if (err instanceof Error) {
        if (err.message.includes('session')) {
          setError('Your session has expired. Please request a new password reset link.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to update password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking session
  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Reset Link</h1>
            <p className="text-gray-600">
              Please wait while we verify your password reset link...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Updated!</h1>
            <p className="text-gray-600 mb-4">
              Your password has been successfully updated.
            </p>
            <p className="text-sm text-gray-500">
              Redirecting you to sign in...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Update Your Password</h1>
          <p className="text-gray-600">Enter your new password below</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <p className="mb-3">{error}</p>
            <button
              onClick={handleRequestNewLink}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors text-sm"
            >
              Request New Reset Link
            </button>
          </div>
        )}

        {hasValidSession && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  placeholder="Enter your new password"
                  required
                  disabled={loading}
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Password must be at least 8 characters long
              </p>
              
              {/* Password strength indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Password strength</span>
                    <span className="text-xs font-medium text-gray-700">{getPasswordStrength(password).label}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${getPasswordStrength(password).color}`}
                      style={{ width: `${getPasswordStrength(password).strength * 25}%` }}
                    ></div>
                  </div>
                  
                  {/* Password requirements checklist */}
                  <ul className="mt-2 text-xs space-y-1">
                    <li className={`flex items-center ${password.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className="mr-1">{password.length >= 8 ? '✓' : '○'}</span>
                      At least 8 characters
                    </li>
                    <li className={`flex items-center ${/[a-z]/.test(password) ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className="mr-1">{/[a-z]/.test(password) ? '✓' : '○'}</span>
                      One lowercase letter
                    </li>
                    <li className={`flex items-center ${/[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className="mr-1">{/[A-Z]/.test(password) ? '✓' : '○'}</span>
                      One uppercase letter
                    </li>
                    <li className={`flex items-center ${/[0-9]/.test(password) ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className="mr-1">{/[0-9]/.test(password) ? '✓' : '○'}</span>
                      One number
                    </li>
                    <li className={`flex items-center ${SPECIAL_CHARS_REGEX.test(password) ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className="mr-1">{SPECIAL_CHARS_REGEX.test(password) ? '✓' : '○'}</span>
                      One special character
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 pr-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  placeholder="Confirm your new password"
                  required
                  disabled={loading}
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !password || !confirmPassword}
              className="w-full bg-wayja-teal text-white px-6 py-2 rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Updating Password...' : 'Update Password'}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Remember your password?{' '}
            <button
              onClick={() => navigate('/auth/login')}
              className="text-wayja-teal hover:text-wayja-teal-dark"
            >
              Sign in here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
} 